import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ContractMainRequest, DocumentType, CurrencyCode, CountryCode } from '@oncampusabroad/api';

const formatDate = (originalDate: string) => {
  const split = originalDate.split("/");
  if (split.length !== 3) return originalDate;
  const day = split[0];
  const month = split[1];
  const year = split[2];
  return `${year}-${month}-${day}`;
};
export enum OrderFlow {
  Flow1,
  Flow2,
}

export enum OrderFlowTabs {
  PersonalDetails,
  TravelDetails,
  IdentificationDetails,
  ExtraPersonalDetails,
  PersonalDetailsConfirmation,
  HealthCard,
  ExtendedInsurance,
  OrderSummary,
  ExtensionFlow,
  Payment,
  Loading,
  Error
}

export interface OrderFlowState extends Omit<ContractMainRequest, 'departureDate' | 'returnDate' | 'birthday'> {
  departureDate: string;
  returnDate: string;
  birthday: string;
  flow2Products: string;
  currentTab: OrderFlowTabs;
  flow: OrderFlow;
}

const initialState: OrderFlowState = {
  firstName: "",
  lastName: "",
  departureDate: "",
  returnDate: "",
  homeCountryCode: CountryCode.Es,
  destinationCountryCode: CountryCode.Es,
  documentType: DocumentType.Passport,
  idCard: "",
  birthday: "",
  phoneNumber: "",
  email: "",
  orderedProducts: [],
  discountCode: "",
  currency: CurrencyCode.Eur,
  withEHIC: true,
  newsConsent: false,
  legalConsentOne: false,
  legalConsentTwo: false,
  flowId: "",
  flow2Products: "",
  flow: OrderFlow.Flow1,
  currentTab: OrderFlowTabs.PersonalDetails,
};

const orderFlowSlice = createSlice({
  name: "orderFlow",
  initialState,
  reducers: {
    setPersonalDetails: (state, action) => {
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
    },
    setTravelDetails: (state, action) => {
      state.homeCountryCode = action.payload.homeCountryCode;
      state.destinationCountryCode = action.payload.destinationCountryCode;
      state.departureDate = action.payload.departureDate;
      state.returnDate = action.payload.returnDate;
    },
    setIdentificationDetails: (state, action) => {
      state.documentType = action.payload.documentType;
      state.idCard = action.payload.idCard;
      state.birthday = action.payload.birthday;
    },
    setExtraPersonalDetails: (state, action) => {
      state.phoneNumber = action.payload.phoneNumber;
      state.email = action.payload.email;
    },
    setNewsConsent: (state, action) => {
      state.newsConsent = action.payload.newsConsent;
    },
    setLegalConsentOne: (state, action) => {
      state.legalConsentOne = action.payload.legalConsentOne;
    },
    setLegalConsentTwo: (state, action) => {
      state.legalConsentTwo = action.payload.legalConsentTwo;
    },
    setHealthCard: (state, action) => {
      state.withEHIC = action.payload.withEHIC;
    },
    setExtendedInsurance: (state, action) => {
      if (action.payload.isFlow2) {
        const newProducts: string[] = [];
        
        if (action.payload.sportsPackage && action.payload.sportsProductId) {
          newProducts.push(action.payload.sportsProductId);
        }
        
        if (action.payload.holidayPackage && action.payload.travelProductId) {
          newProducts.push(action.payload.travelProductId);
        }
        
        state.orderedProducts = newProducts;
      } else {
        const newOrderedProducts = [...state.orderedProducts];
        
        if (action.payload.holidayPackage && !newOrderedProducts.includes('TRAVEL2024')) {
          newOrderedProducts.push('TRAVEL2024');
        } else if (!action.payload.holidayPackage) {
          const index = newOrderedProducts.indexOf('TRAVEL2024');
          if (index !== -1) {
            newOrderedProducts.splice(index, 1);
          }
        }
        
        if (action.payload.sportsPackage && !newOrderedProducts.includes('SPORTS2024')) {
          newOrderedProducts.push('SPORTS2024');
        } else if (!action.payload.sportsPackage) {
          const index = newOrderedProducts.indexOf('SPORTS2024');
          if (index !== -1) {
            newOrderedProducts.splice(index, 1);
          }
        }
        
        state.orderedProducts = newOrderedProducts;
      }
    },
    setDiscount: (state, action) => {
      state.discountCode = action.payload.discountCode;
    },
    setTab: (state, action: PayloadAction<OrderFlowTabs>) => {
      state.currentTab = action.payload;
    },
    setProducts: (state, action) => {
      const productsString = action.payload.products;
      let productsArray: string[] = [];
      
    
      if (productsString) {
        if (productsString.includes('1')) {
          productsArray.push('MAIN2024');
        }
        if (productsString.includes('2')) {
          productsArray.push('SPORTS2024');
        }
        if (productsString.includes('4')) {
          productsArray.push('TRAVEL2024');
        }
      }
      
      state.orderedProducts = productsArray;
    },
    setFlow: (state, action) => {
      state.flow = action.payload.flow;
    },
    setOrderDetails: (state, action) => {
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
      state.departureDate = formatDate(action.payload.departureDate);
      state.returnDate = formatDate(action.payload.returnDate);
      state.homeCountryCode = action.payload.homeCountry;
      state.destinationCountryCode = action.payload.destinationCountry;
      state.documentType = action.payload.documentType;
      state.idCard = action.payload.documentNumber;
      state.birthday = formatDate(action.payload.birthday);
      state.phoneNumber = action.payload.phoneNumber;
      state.email = action.payload.email;
      
      let productsArray: string[] = [];
      
      if (Array.isArray(action.payload.products)) {
        productsArray = action.payload.products;
      } else if (typeof action.payload.products === 'string') {
        const productsString = action.payload.products;
        if (productsString.includes('1')) {
          productsArray.push('MAIN2024');
        }
        if (productsString.includes('2')) {
          productsArray.push('SPORTS2024');
        }
        if (productsString.includes('4')) {
          productsArray.push('TRAVEL2024');
        }
      }
      
      state.orderedProducts = productsArray;
      state.discountCode = action.payload.discountCode;
      state.withEHIC = action.payload.healthInsurance;
      state.newsConsent = action.payload.newsConsent;
      state.legalConsentOne = action.payload.legalConsentOne;
      state.legalConsentTwo = action.payload.legalConsentTwo;
      state.flow2Products = Array.isArray(action.payload.originalProducts) 
        ? JSON.stringify(action.payload.originalProducts) 
        : action.payload.products;
      state.flow = OrderFlow.Flow2;
      state.flowId = "";
      
      state.currency = "EUR";
    },
    resetOrderFlow: (state) => {
      return initialState;
    },
  },
});
export const {
  setPersonalDetails,
  setTravelDetails,
  setIdentificationDetails,
  setExtraPersonalDetails,
  setNewsConsent,
  setLegalConsentOne,
  setLegalConsentTwo,
  setHealthCard,
  setExtendedInsurance,
  setDiscount,
  setTab,
  setProducts,
  resetOrderFlow,
  setFlow,
  setOrderDetails,
} = orderFlowSlice.actions;
export default orderFlowSlice.reducer;
